import { useState, useEffect } from 'react';
import AddTeamModal from '../modals/AddTeamModal';
import EditTeamModal from '../modals/EditTeamModal';
import { getTeams, upsertTeam, deleteTeam } from '../../utils/api';

export default function AdminTeams({ openTeamPanel, onViewAsTeamLead, onRolesSaved, showToast }) {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editTeam, setEditTeam] = useState(null);

  function loadTeams() {
    setLoading(true);
    getTeams()
      .then(data => setTeams(data || []))
      .catch(err => console.error('Failed to load teams:', err))
      .finally(() => setLoading(false));
  }

  useEffect(() => { loadTeams(); }, []);

  async function handleAddTeam(team) {
    // CREATE — needs server ID, so must wait
    const saved = await upsertTeam(team);
    showToast('Team added.');
    loadTeams();
    return saved;
  }

  async function handleEditTeam(team) {
    const prev = teams;
    setTeams(t => t.map(t => t.id === team.id ? { ...t, ...team } : t));
    // Don't close here — let the modal close itself after saveTeamRoles completes
    showToast('Team saved.');
    try {
      await upsertTeam(team);
    } catch (err) {
      setTeams(prev);
      showToast('Error saving team — changes reverted.');
      console.error(err);
    }
  }

  async function handleDeleteTeam(id) {
    const prev = teams;
    setTeams(t => t.filter(t => t.id !== id));
    setEditOpen(false);
    showToast('Team deleted.');
    try {
      await deleteTeam(id);
    } catch (err) {
      setTeams(prev);
      showToast('Error deleting team — please try again.');
      console.error(err);
    }
  }

  return (
    <>
      <div className="page-content">
        <div className="page-title-row">
          <div>
            <div className="page-title">All Teams</div>
            <div className="page-sub">{teams.length} teams · 2026 Conference</div>
          </div>
          <button className="btn btn-dark" onClick={() => setAddOpen(true)}>+ Add Team</button>
        </div>

        <div className="panel">
          {loading ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--muted)' }}>Loading teams…</div>
          ) : teams.length === 0 ? (
            <div style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--muted)', fontFamily: 'var(--font-b)', fontWeight: 300 }}>
              No teams yet. Click <strong style={{ fontFamily: 'var(--font-h)', color: 'var(--black)' }}>+ Add Team</strong> to create your first team.
            </div>
          ) : (
            <div className="team-grid">
              {teams.map(team => (
                <div key={team.id} className="team-card" onClick={() => openTeamPanel(team)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div className="team-card-name">{team.name}</div>
                    <button
                      className="btn btn-xs"
                      style={{ flexShrink: 0 }}
                      onClick={e => { e.stopPropagation(); setEditTeam(team); setEditOpen(true); }}
                    >
                      Edit
                    </button>
                  </div>
                  <div className="team-card-meta">Lead: {team.leadEmail || 'Unassigned'}</div>
                  <div className="team-card-badge">{team.passLevel || 'SL'}</div>
                  <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <span className="status-dot dot-green" style={{ margin: 0 }} />
                      <span style={{ fontSize: 12, fontFamily: 'var(--font-b)', fontWeight: 300, color: 'var(--muted)' }}>
                        Active
                      </span>
                    </div>
                    {onViewAsTeamLead && (
                      <button
                        className="btn btn-xs"
                        style={{ fontSize: 11 }}
                        onClick={e => { e.stopPropagation(); onViewAsTeamLead(team); }}
                      >
                        View as Team Lead
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <AddTeamModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSave={handleAddTeam}
        showToast={showToast}
      />
      <EditTeamModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        team={editTeam}
        allTeams={teams}
        onSave={handleEditTeam}
        onDelete={handleDeleteTeam}
        onMerged={() => { setEditOpen(false); loadTeams(); }}
        onRolesSaved={onRolesSaved}
        showToast={showToast}
      />
    </>
  );
}
